import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { RootState } from '../../../../../infrastructure/redux/store';

import {
  AlocacaoPageQuery_cursos,
  AlocacaoPageQuery_disciplinas,
  AlocacaoPageQuery_horarios,
  AlocacaoPageQuery_periodos,
  AlocacaoPageQuery_professores,
} from '../../graphql/queries/__generated__/AlocacaoPageQuery';

import {
  alternarAtivos,
  confirmarAlocacao,
  inserirAlocacao,
  limparSelecao,
  removerAlocacao,
  removerSelecaoAlocacao,
  selecionarAlocacao,
  selecionarDisciplina,
  selecionarHorario,
  selecionarPeriodo,
  substituirSelecaoAlocacao,
} from '../../reducers/alocacao.reducer';

import { useAlocarProfessorMutation } from '../../graphql/mutations/alocar_professor.mutation';
import { useDesalocarProfessorMutation } from '../../graphql/mutations/desalocar_professor.mutation';
import {
  validar_alocacao,
  validar_alocacoes,
  validar_desalocacao,
  validar_planejamento,
} from '../../utils/validators/alocacao.validator';
import { reticencias } from '../../../shared/utils/formatters/reticencias.formatter';
import { FormGroup } from '../../../shared/components/form_components/group';
import { Button } from '../../../shared/components/form_components/button';
import { ButtonSelect } from '../../../shared/components/button_select';
import { ItemProps } from '../../../shared/components/button_select/modal';
import { Professores } from './menu_professores';
import { Turmas } from './menu_turmas';
import { Dias } from './menu_dias';
import { Container } from './styles';
import { LayoutGroup } from '../../../shared/components/layout_components/group';
import { ButtonToggle } from '../../../shared/components/button_toggle';
import { useConfirmarAlocacaoMutation } from '../../graphql/mutations/confirmar_alocacao.mutation';
import { useValidarAlocacaoLazyQuery } from '../../graphql/queries/validar_alocacao.query';

interface Props {
  periodos?: AlocacaoPageQuery_periodos[];
  professores?: AlocacaoPageQuery_professores[];
  disciplinas?: AlocacaoPageQuery_disciplinas[];
  cursos?: AlocacaoPageQuery_cursos[];
  horarios?: AlocacaoPageQuery_horarios[];
}

const Menu: React.FC<Props> = (props) => {
  const { theme } = useSelector((state: RootState) => state.theme);
  const { selecao } = useSelector((state: RootState) => state.alocacao);

  const [periodosModal, setPeriodosModal] = React.useState(false);
  const [disciplinasModal, setDisciplinasModal] = React.useState(false);
  const [horariosModal, setHorariosModal] = React.useState(false);
  const [bloquearBotoes, setBloquearBotoes] = React.useState(false);

  const dispatch = useDispatch();

  const [alocar_professor] = useAlocarProfessorMutation();
  const [confirmar_alocacao] = useConfirmarAlocacaoMutation();
  const [desalocar_professor] = useDesalocarProfessorMutation();
  const [obter_alocacao, { data: alocacao_cache, refetch: refetch_obter_alocacao }] =
    useValidarAlocacaoLazyQuery();

  const alternar = {
    periodos: () => setPeriodosModal(!periodosModal),
    disciplinas: () => setDisciplinasModal(!disciplinasModal),
    horarios: () => setHorariosModal(!horariosModal),
  };

  const selecionar = {
    periodo: (item: ItemProps<AlocacaoPageQuery_periodos>) => {
      dispatch(selecionarPeriodo(item.data));
      alternar.periodos();
    },
    disciplina: (item: ItemProps<AlocacaoPageQuery_disciplinas>) => {
      dispatch(selecionarDisciplina(item.data));
      alternar.disciplinas();
    },
    horario: (item: ItemProps<AlocacaoPageQuery_horarios>) => {
      dispatch(selecionarHorario(item.data));
      alternar.horarios();
    },
    ativos: () => {
      dispatch(alternarAtivos());
    },
  };

  const alocar = async (planejamento: boolean) => {
    setBloquearBotoes(() => true);

    const validacao_alocacao = await validar_alocacoes(
      selecao,
      alocacao_cache,
      obter_alocacao,
      refetch_obter_alocacao
    );

    if (validacao_alocacao) {
      setBloquearBotoes(() => false);
      return alert(validacao_alocacao);
    }

    for (const selec_alocacao of selecao.alocacoes) {
      if (selec_alocacao.alocacao.id_alocacao) {
        const { data } = await confirmar_alocacao({
          variables: {
            id_alocacao: selec_alocacao.alocacao.id_alocacao,
          },
        });

        if (data) {
          dispatch(confirmarAlocacao(data.confirmarAlocacao));
        }

        continue;
      }

      if (selecao.professor && selecao.disciplina && selecao.horario) {
        const { data } = await alocar_professor({
          variables: {
            id_disciplina: selecao.disciplina.id_disciplina,
            id_horario: selecao.horario.id_horario,
            id_professor: selecao.professor.id_professor,
            dat_alocacao: selec_alocacao.alocacao.dat_alocacao,
            id_oferta: selec_alocacao.id_oferta,
            id_turma: selec_alocacao.id_turma,
            ind_status: selecao.ind_status,
            planejamento,
          },
        });

        if (data) {
          dispatch(inserirAlocacao(data.alocarProfessor));
        }
      }
    }

    dispatch(limparSelecao('alocar'));
    setBloquearBotoes(() => false);
  };

  const desalocar = async () => {
    setBloquearBotoes(() => true);

    for (const selec_alocacao of selecao.alocacoes) {
      if (!selec_alocacao.alocacao.id_alocacao) {
        continue;
      }

      const { data } = await desalocar_professor({
        variables: {
          id_alocacao: selec_alocacao.alocacao.id_alocacao,
        },
      });

      if (data) {
        dispatch(
          removerAlocacao({
            id_alocacao: selec_alocacao.alocacao.id_alocacao,
            id_oferta: selec_alocacao.id_oferta,
            id_turma: selec_alocacao.id_turma,
          })
        );

        dispatch(
          substituirSelecaoAlocacao({
            id_oferta: selec_alocacao.id_oferta,
            id_turma: selec_alocacao.id_turma,
            alocacao: {
              ...selec_alocacao.alocacao,
              id_alocacao: undefined,
              professor: undefined,
              disciplina: undefined,
              planejamento: false,
            },
          })
        );
      }
    }

    dispatch(limparSelecao('desalocar'));
    setBloquearBotoes(false);
  };

  // selecionar periodo atual
  useEffect(() => {
    if (props.periodos) {
      const ano_atual = new Date().getFullYear();

      const periodos_atuais = props.periodos.filter(
        (periodo) => new Date(periodo.dat_inicio).getFullYear() === ano_atual
      );

      const primeiro_periodo = periodos_atuais[0];
      const ultimo_periodo = periodos_atuais[1];

      if (primeiro_periodo && ultimo_periodo) {
        const dat_fim_primeiro_periodo = new Date(primeiro_periodo.dat_fim);
        const data_atual = new Date();

        if (data_atual.getTime() < dat_fim_primeiro_periodo.getTime()) {
          dispatch(selecionarPeriodo(primeiro_periodo));
        } else {
          dispatch(selecionarPeriodo(ultimo_periodo));
        }
      }
    }
  }, [props.periodos]);

  return (
    <Container>
      <LayoutGroup>
        <ButtonSelect
          modalTitle={'Período'}
          modalVisible={periodosModal}
          onClick={alternar.periodos}
          onSelectItem={selecionar.periodo}
          backgroundColor={theme.colors.primary}
          textColor={theme.colors.textPrimary}
          items={props.periodos?.map((p) => ({
            id: p.id_periodo,
            label: p.dsc_periodo,
            data: p,
          }))}
        >
          {selecao.periodo?.dsc_periodo || 'Selecionar período'}
        </ButtonSelect>
        <ButtonToggle
          active={selecao.ativos}
          onClick={selecionar.ativos}
          backgroundColor={theme.colors.primary}
          textColor={theme.colors.textPrimary}
        >
          Ativos
        </ButtonToggle>
      </LayoutGroup>

      <Turmas cursos={props.cursos} periodo={selecao.periodo} ativos={selecao.ativos} />

      <Dias />

      <Professores professores={props.professores} />

      <FormGroup>
        <ButtonSelect
          modalTitle={'Disciplinas'}
          modalVisible={disciplinasModal}
          onClick={alternar.disciplinas}
          onSelectItem={selecionar.disciplina}
          backgroundColor={theme.colors.primary}
          textColor={theme.colors.textPrimary}
          items={props.disciplinas?.map((d) => ({
            id: d.id_disciplina,
            label: `${d.dsc_nome_disciplina} - ${d.dsc_sigla} - ${d.num_carga_horaria}h`,
            data: d,
          }))}
        >
          {reticencias(selecao.disciplina?.dsc_nome_disciplina, 20) || 'Selecionar disciplina'}
        </ButtonSelect>

        <ButtonSelect
          modalTitle={'Horários'}
          modalVisible={horariosModal}
          onClick={alternar.horarios}
          onSelectItem={selecionar.horario}
          backgroundColor={theme.colors.primary}
          textColor={theme.colors.textPrimary}
          items={props.horarios?.map((h) => ({
            id: h.id_horario,
            label: `${h.hora_inicio}h - ${h.hora_fim}h`,
            data: h,
          }))}
        >
          {selecao.horario
            ? `${selecao.horario.hora_inicio}h - ${selecao.horario.hora_fim}h`
            : 'Selecionar horário'}
        </ButtonSelect>
      </FormGroup>

      <FormGroup>
        <Button
          color={theme.colors.primary}
          onClick={() => alocar(true)}
          disabled={!validar_planejamento(selecao) || bloquearBotoes}
        >
          Planejar
        </Button>
        <Button
          color={theme.colors.primary}
          onClick={() => alocar(false)}
          disabled={!validar_alocacao(selecao) || bloquearBotoes}
        >
          Alocar
        </Button>
        <Button
          color={theme.colors.error}
          onClick={desalocar}
          disabled={!validar_desalocacao(selecao) || bloquearBotoes}
        >
          Desalocar
        </Button>
      </FormGroup>
    </Container>
  );
};

export { Menu };
